import axios from "axios";
import ServerUrl from "../services/ServerUrl";
import getSessionToken from "../utils/getSessionToken";
import { removeItem } from "../utils/cooke-data";
import { toast } from "sonner";

const apiClient = axios.create({
    baseURL: ServerUrl,
    headers: {
        Accept: "application/json",
    },
});

apiClient.interceptors.request.use((config) => {
    const token = getSessionToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Interceptor 1: Convert success: false responses (HTTP 200) into a rejected promise
apiClient.interceptors.response.use(
    (response) => {
        if (response.data && response.data.success === false) {
            const error = new Error(response.data.message || "Operation failed");
            error.response = response;
            error.config = response.config;
            return Promise.reject(error);
        }
        return response;
    }
);

// Interceptor 2: Handle all errors (HTTP errors and converted success: false errors), show toast, and return a friendly rejected error
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        let errorMessage = "An unexpected error occurred.";

        if (error.response) {
            if (error.response.status === 401) {
                removeItem("session_token");
                removeItem("user");
                removeItem("userRole");
                removeItem("permissions");
                window.location.href = "/login";
                return Promise.reject(new Error("Unauthorized"));
            }

            const responseData = error.response.data;

            // Try to extract the first specific field-level validation error
            // API returns: { success: false, message: "Validation error", data: { "field.0": ["msg1", "msg2"], ... } }
            const fieldErrors = responseData?.data;
            if (fieldErrors && typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
                const firstFieldMessages = Object.values(fieldErrors)[0];
                if (Array.isArray(firstFieldMessages) && firstFieldMessages.length > 0) {
                    errorMessage = firstFieldMessages[0];
                } else if (typeof firstFieldMessages === "string") {
                    errorMessage = firstFieldMessages;
                } else {
                    errorMessage = responseData?.message || responseData?.error || `Error: ${error.response.status || 'unknown'}`;
                }
            } else {
                errorMessage = responseData?.message || responseData?.error || `Error: ${error.response.status || 'unknown'}`;
            }
        } else if (error.request) {
            errorMessage = "No response from server. Please check your connection.";
        } else {
            errorMessage = error.message;
        }

        toast.error(errorMessage, {
            id: `api-error-${error.config?.url}`,
            duration: 4000,
        });

        // Reject with a friendly Error containing the extracted errorMessage so catch blocks can access it easily
        const friendlyError = new Error(errorMessage);
        friendlyError.config = error.config;
        friendlyError.response = error.response;
        return Promise.reject(friendlyError);
    }
);

export default apiClient;
